/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { createEventEmitter, emitEvent, TSimpleEventEmitter } from "@adapptio/lib-shared";
import { v4 as uuid_v4 } from "uuid";

import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";
import { IToast, TOAST_TYPE } from "./IToast";
import { ErrorTracker } from "@adapptio/lib-components";

/**
 * Toast entry states
 */
export enum TOAST_ENTRY_STATE {
	INIT = "init",
	READY = "ready",
	REMOVED = "removed"
}

/**
 * Toast entry
 */
export interface IToastEntry {
	readonly id: string;
	config: IToast;
	state: TOAST_ENTRY_STATE;
	stateTransitionTimeout: number;
	readonly onChange: TSimpleEventEmitter<void>;
}

interface IToastEntryIndex {
	[K: string]: IToastEntry;
}

const TOAST_OPEN_DELAY = 10;
const TOAST_CLOSE_DELAY = 400;

/**
 * Service options
 */
export interface IToastServiceOpts {}

/**
 * Service to manage commands
 */
export class ToastService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** Toast entries */
	private toasts: IToastEntry[] = [];

	/** ID index */
	private idIndex: IToastEntryIndex = {};

	/** Emitted when entries list has changed */
	public readonly onEntriesChange = createEventEmitter<void>();

	/** Emitted when a toast was add */
	public readonly onToastAdd = createEventEmitter<IToastEntry>();

	/** Emitted when a toast has been removed */
	public readonly onToastRemove = createEventEmitter<IToastEntry>();

	/**
	 * Service Constructor
	 *
	 * @param dc Dependency Container
	 * @param opts Configuration options
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public constructor(private dc: IEditor, opts: IToastServiceOpts) {
		this.logger = dc.get("logger").facility("ToastService");
	}

	/**
	 * Adds a toast and returns it's ID
	 *
	 * @param config Toast configuration
	 */
	public add(config: IToast): string {
		const id = uuid_v4();

		this.logger.debug(`Adding toast with ID '${id}' and config:`, config);

		// Create new entry
		const entry: IToastEntry = {
			id: id,
			config: config,
			state: TOAST_ENTRY_STATE.INIT,
			stateTransitionTimeout: null,
			onChange: createEventEmitter()
		};

		this.toasts.push(entry);
		this.idIndex[id] = entry;

		entry.stateTransitionTimeout = setTimeout(() => {
			this.logger.debug(`Toast ID '${id}' state changed to READY.`);

			entry.state = TOAST_ENTRY_STATE.READY;
			emitEvent(entry.onChange);

			this.resetTimeout(id);

			try {
				emitEvent(this.onToastAdd, entry);
			} catch (err) {
				this.logger.warn(`Failed to handle onToastAdd event for toast with ID '${id}':`, err);
			}
		}, TOAST_OPEN_DELAY) as unknown as number;

		emitEvent(this.onEntriesChange);

		return id;
	}

	/**
	 * Tries to remove a toast and returns if it was removed
	 * If toast with given ID does not exists false is returned.
	 *
	 * @param toastId Toast ID
	 */
	public remove(toastId: string): boolean {
		const entry = this.idIndex[toastId];

		if (!entry || entry.state === TOAST_ENTRY_STATE.REMOVED) {
			return false;
		}

		if (entry.stateTransitionTimeout) {
			clearTimeout(entry.stateTransitionTimeout);
		}

		this.logger.debug(`Toast ID '${toastId}' state changed to CLOSED.`);

		entry.state = TOAST_ENTRY_STATE.REMOVED;
		emitEvent(entry.onChange);

		try {
			emitEvent(this.onToastRemove, entry);
		} catch (err) {
			this.logger.warn(`Failed to handle onToastRemove event for toast with ID '${toastId}':`, err);
		}

		entry.stateTransitionTimeout = setTimeout(() => {
			const i = this.toasts.indexOf(entry);

			if (i >= 0) {
				this.toasts.splice(i, 1);
			}

			delete this.idIndex[toastId];

			emitEvent(this.onEntriesChange);
		}, TOAST_CLOSE_DELAY) as unknown as number;

		return true;
	}

	/**
	 * Tries to reset a close timeout and returns true if toast was found.
	 *
	 * @param toastId Toast ID
	 */
	public resetTimeout(toastId: string): boolean {
		const entry = this.idIndex[toastId];

		if (!entry || entry.state === TOAST_ENTRY_STATE.REMOVED) {
			return false;
		}

		if (entry.state === TOAST_ENTRY_STATE.INIT) {
			return true;
		}

		if (entry.stateTransitionTimeout) {
			clearTimeout(entry.stateTransitionTimeout);
		}

		if (entry.config.timeoutMs > 0) {
			entry.stateTransitionTimeout = setTimeout(() => {
				this.remove(toastId);
			}, entry.config.timeoutMs) as unknown as number;
		}

		return true;
	}

	/**
	 * Tries to disable a close timeout and returns true if toast was found.
	 *
	 * @param toastId Toast ID
	 */
	public disableTimeout(toastId: string): boolean {
		const entry = this.idIndex[toastId];

		if (!entry || entry.state === TOAST_ENTRY_STATE.REMOVED) {
			return false;
		}

		if (entry.state === TOAST_ENTRY_STATE.INIT) {
			return true;
		}

		if (entry.stateTransitionTimeout) {
			clearTimeout(entry.stateTransitionTimeout);
		}

		return true;
	}

	/**
	 * Tries to update a toast config and returns true if toast was found
	 * Toast config will be merged with the existing one so only changed properties
	 * may be provided.
	 *
	 * @param toastId Toast ID
	 * @param config Config
	 * @param resetTimeout If to reset timeout
	 */
	public update(toastId: string, config: Partial<IToast>, resetTimeout = true): boolean {
		const entry = this.idIndex[toastId];

		if (!entry || entry.state === TOAST_ENTRY_STATE.REMOVED) {
			return false;
		}

		entry.config = {
			...entry.config,
			...config
		};

		emitEvent(entry.onChange);

		if (resetTimeout) {
			this.resetTimeout(toastId);
		}

		return true;
	}

	/**
	 * Returns toast config by ID
	 *
	 * @param toastId Toast ID
	 */
	public getById(toastId: string): IToast {
		return this.idIndex[toastId]?.config || null;
	}

	/**
	 * Returns current toast entries
	 */
	public getEntries(): IToastEntry[] {
		return this.toasts;
	}
}
